import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// #95: CSS-`truncate`/`line-clamp` klippt nur VISUELL — Screenreader lesen den vollen Text
// weiterhin. Was fehlt, ist der sehende Maus-/Tastaturnutzer: dafür diese Blase. Natives `title`
// wäre per Tastatur unerreichbar (genau die Klage des Issues), deshalb ein echtes Muster:
// Hover UND Fokus (React onFocus = focusin, bubbelt vom fokussierbaren Kind hoch) öffnen,
// Escape/Blur/MouseLeave schließen. Die Blase erscheint NUR, wenn das [data-clip]-Element
// tatsächlich geklippt ist. Sie wird nur bei Sichtbarkeit GERENDERT, damit aria-describedby
// nie auf eine nicht existierende ID zeigt.
// #107: Die Blase hängt als Portal an document.body und positioniert sich fixed aus dem
// Anker-Rect — innerhalb der overflow-Scroll-Panels wurde sie an der Klippkante abgeschnitten
// (sobald eine overflow-Achse nicht `visible` ist, klippt auch die andere). Position wird beim
// Öffnen berechnet; Scroll/Resize schließen die Blase, statt sie mitzuführen (MouseLeave/Blur
// feuern dabei ohnehin in fast allen Fällen — schließen ist das ehrliche, einfache Verhalten).
export function isTextClipped(el: HTMLElement): boolean {
  return el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;
}

const MAX_WIDTH = 420;
const EDGE_GAP = 8;

export function ClipTooltip({
  text,
  children,
  className,
}: {
  text: string;
  children: ReactNode;
  className?: string;
}) {
  const id = useId();
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; maxWidth: number } | null>(null);

  const tryOpen = () => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const clipped = wrapper.querySelector<HTMLElement>('[data-clip]');
    if (!clipped || !isTextClipped(clipped)) return;
    const rect = wrapper.getBoundingClientRect();
    const maxWidth = Math.min(MAX_WIDTH, window.innerWidth - 2 * EDGE_GAP);
    // linksbündig am Anker; nach links einrücken, wenn die Blase rechts über den Viewport liefe
    const left = Math.max(EDGE_GAP, Math.min(rect.left, window.innerWidth - maxWidth - EDGE_GAP));
    setPos({ top: rect.bottom + 4, left, maxWidth });
  };
  const close = () => setPos(null);

  // Final-Review #107: Der Kommentar oben verspricht Schließen bei Scroll/Resize — hier die
  // Listener dazu. Nur aktiv, solange die Blase offen ist; capture fängt auch Panel-Scrolls.
  useEffect(() => {
    if (!pos) return;
    const onScrollOrResize = () => setPos(null);
    window.addEventListener('scroll', onScrollOrResize, { capture: true, passive: true });
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, { capture: true });
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [pos]);

  return (
    <span
      ref={wrapperRef}
      className={`relative ${className ?? ''}`}
      onMouseEnter={tryOpen}
      onMouseLeave={close}
      onFocus={tryOpen}
      onBlur={close}
      onKeyDown={(e) => {
        if (e.key === 'Escape') close();
      }}
      aria-describedby={pos ? id : undefined}
    >
      {children}
      {pos &&
        createPortal(
          <span
            role="tooltip"
            id={id}
            style={{ top: pos.top, left: pos.left, maxWidth: pos.maxWidth }}
            className="fixed z-50 whitespace-normal break-words rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-xs text-text-body shadow-md"
          >
            {text}
          </span>,
          document.body,
        )}
    </span>
  );
}
